import React, { useState, useEffect, createContext, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import Header from '../layout/Header';
import FragranceGrid from '../fragrance/FragranceGrid';
import StatsDashboard from '../dashboard/StatsDashboard';
import SearchAndFilter from '../fragrance/SearchAndFilter';
import LoadingSpinner from '../ui/LoadingSpinner';
import Button from '../ui/Button';
import { Fragrance } from '../fragrance/FragranceCard';

interface PublicCollection {
  user: {
    username: string;
    memberSince: string;
  };
  fragrances: Fragrance[];
  stats: any;
  statsPrivacy: {
    public_stats_overview: boolean;
    public_stats_insights: boolean;
    public_stats_ratings: boolean;
    public_stats_seasonal: boolean;
    public_stats_houses: boolean;
    public_stats_concentrations: boolean;
    public_stats_notes: boolean;
  };
}

// Create a mock fragrance context for public view
const PublicFragranceContext = createContext<any>(null);

// Override the useFragrances hook for public view
const usePublicFragrances = () => {
  const publicContext = useContext(PublicFragranceContext);
  return publicContext;
};

const PublicFragranceProvider: React.FC<{ children: React.ReactNode; collection: PublicCollection }> = ({ children, collection }) => {
  const [filteredFragrances, setFilteredFragrances] = useState<Fragrance[]>([]);
  const [filters, setFilters] = useState({});
  const [sorting, setSorting] = useState({ sort_by: 'house', sort_order: 'asc' });
  const [pageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFiltering, setIsFiltering] = useState(false);

  const fetchFragrances = async (customFilters?: any, customSorting?: any) => {
    setIsFiltering(true);
    
    // Use requestIdleCallback for better performance
    const processFiltering = () => {
      // Client-side filtering and sorting for public collections
      let filtered = [...collection.fragrances];
      
      const activeFilters = customFilters || filters;
      const activeSorting = customSorting || sorting;
    
    // Apply search filter
    if (activeFilters?.search) {
      filtered = filtered.filter(f => 
        f.name.toLowerCase().includes(activeFilters.search.toLowerCase()) ||
        f.house.toLowerCase().includes(activeFilters.search.toLowerCase())
      );
    }
    
    // Apply house filter
    if (activeFilters?.house) {
      filtered = filtered.filter(f => 
        f.house.toLowerCase().includes(activeFilters.house.toLowerCase())
      );
    }
    
    // Apply notes filter
    if (activeFilters?.notes) {
      filtered = filtered.filter(f => 
        (f.notes_top && f.notes_top.toLowerCase().includes(activeFilters.notes.toLowerCase())) ||
        (f.notes_middle && f.notes_middle.toLowerCase().includes(activeFilters.notes.toLowerCase())) ||
        (f.notes_base && f.notes_base.toLowerCase().includes(activeFilters.notes.toLowerCase()))
      );
    }
    
    // Apply concentration filter
    if (activeFilters?.concentration) {
      filtered = filtered.filter(f => f.concentration === activeFilters.concentration);
    }
    
    // Apply favorite filter
    if (activeFilters?.is_favorite !== undefined) {
      filtered = filtered.filter(f => Boolean(f.is_favorite) === Boolean(activeFilters.is_favorite));
    }
    
    // Apply decant filter
    if (activeFilters?.is_decant !== undefined) {
      filtered = filtered.filter(f => Boolean(f.is_decant) === Boolean(activeFilters.is_decant));
    }
    
    // Apply rating filters
    if (activeFilters?.min_rating) {
      filtered = filtered.filter(f => f.overall_rating && f.overall_rating >= activeFilters.min_rating);
    }
    
    if (activeFilters?.max_rating) {
      filtered = filtered.filter(f => f.overall_rating && f.overall_rating <= activeFilters.max_rating);
    }

    // Apply seasonality filters (≥3 rating)
    if (activeFilters?.winter_rating) {
      filtered = filtered.filter(f => f.winter_rating && f.winter_rating >= 3);
    }

    if (activeFilters?.spring_rating) {
      filtered = filtered.filter(f => f.spring_rating && f.spring_rating >= 3);
    }

    if (activeFilters?.summer_rating) {
      filtered = filtered.filter(f => f.summer_rating && f.summer_rating >= 3);
    }

    if (activeFilters?.fall_rating) {
      filtered = filtered.filter(f => f.fall_rating && f.fall_rating >= 3);
    }

    // Apply day/night filters (≥3 rating)
    if (activeFilters?.day_rating) {
      filtered = filtered.filter(f => f.day_rating && f.day_rating >= 3);
    }

    if (activeFilters?.night_rating) {
      filtered = filtered.filter(f => f.night_rating && f.night_rating >= 3);
    }
    
      // Apply sorting with house name secondary sort for ratings
      const sortBy = activeSorting?.sort_by || sorting.sort_by;
      const sortOrder = activeSorting?.sort_order || sorting.sort_order;
      
      filtered.sort((a, b) => {
        let aVal = a[sortBy as keyof Fragrance];
        let bVal = b[sortBy as keyof Fragrance];
        
        // Handle undefined values
        if (aVal === undefined) aVal = '';
        if (bVal === undefined) bVal = '';
        
        if (typeof aVal === 'string') aVal = aVal.toLowerCase();
        if (typeof bVal === 'string') bVal = bVal.toLowerCase();
        
        let comparison = 0;
        if (aVal < bVal) comparison = sortOrder === 'asc' ? -1 : 1;
        else if (aVal > bVal) comparison = sortOrder === 'asc' ? 1 : -1;
        
        // For rating fields, use house name as secondary sort
        if (comparison === 0 && ['overall_rating', 'longevity', 'sillage', 'winter_rating', 'spring_rating', 'summer_rating', 'fall_rating', 'day_rating', 'night_rating'].includes(sortBy)) {
          const aHouse = a.house.toLowerCase();
          const bHouse = b.house.toLowerCase();
          if (aHouse < bHouse) return -1;
          if (aHouse > bHouse) return 1;
        }
        
        return comparison;
      });
    
      // Apply pagination
      const totalItems = filtered.length;
      const totalPages = Math.ceil(totalItems / pageSize);
      const startIndex = (currentPage - 1) * pageSize;
      const paginatedFragrances = filtered.slice(startIndex, startIndex + pageSize);
      
      setFilteredFragrances(paginatedFragrances);
      setTotalFilteredItems(totalItems);
      
      // Update local state
      if (customFilters) setFilters(customFilters);
      if (customSorting) setSorting(customSorting);
      
      // Reset to page 1 if current page is beyond total pages
      if (currentPage > totalPages && totalPages > 0) {
        setCurrentPage(1);
      }
      
      setIsFiltering(false);
    };

    // Use requestIdleCallback for performance, fallback to setTimeout
    if (window.requestIdleCallback) {
      window.requestIdleCallback(processFiltering, { timeout: 100 });
    } else {
      setTimeout(processFiltering, 0);
    }
  };

  // Initial load effect
  useEffect(() => {
    fetchFragrances({}, { sort_by: 'house', sort_order: 'asc' });
  }, []);

  useEffect(() => {
    fetchFragrances(filters, sorting);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const getAutocompleteSuggestions = async (type: string, query: string = '') => {
    // Client-side autocomplete for public collections
    const suggestions: string[] = [];
    
    if (type === 'house') {
      const allHouses = collection.fragrances.map(f => f.house);
      const uniqueHouses = allHouses.filter((item, index) => allHouses.indexOf(item) === index);
      return uniqueHouses.filter(h => h.toLowerCase().includes(query.toLowerCase())).slice(0, 10);
    }
    
    if (type === 'note') {
      const notes: string[] = [];
      collection.fragrances.forEach(f => {
        if (f.notes_top) notes.push(...f.notes_top.split(',').map(n => n.trim()));
        if (f.notes_middle) notes.push(...f.notes_middle.split(',').map(n => n.trim()));
        if (f.notes_base) notes.push(...f.notes_base.split(',').map(n => n.trim()));
      });
      const uniqueNotes = notes.filter((item, index) => notes.indexOf(item) === index);
      return uniqueNotes.filter(n => n.toLowerCase().includes(query.toLowerCase())).slice(0, 10);
    }
    
    if (type === 'fragrance_name') {
      const allNames = collection.fragrances.map(f => f.name);
      const uniqueNames = allNames.filter((item, index) => allNames.indexOf(item) === index);
      return uniqueNames.filter(n => n.toLowerCase().includes(query.toLowerCase())).slice(0, 10);
    }
    
    return [];
  };

  // Calculate pagination based on current filtered results
  const [totalFilteredItems, setTotalFilteredItems] = useState(collection.fragrances.length);
  const totalPages = Math.ceil(totalFilteredItems / pageSize);

  const value = {
    collection: {
      fragrances: filteredFragrances,
      pagination: { 
        page: currentPage, 
        limit: pageSize, 
        total: totalFilteredItems, 
        totalPages: totalPages, 
        hasNext: currentPage < totalPages, 
        hasPrev: currentPage > 1 
      },
      statsPrivacy: collection.statsPrivacy // Pass through the privacy settings
    },
    stats: collection.stats,
    isLoading: isFiltering,
    error: null,
    filters,
    sorting,
    pageSize,
    currentPage,
    setCurrentPage,
    fetchFragrances,
    getAutocompleteSuggestions,
    setFilters,
    setSorting,
    setPageSize: () => {},
    clearFilters: () => setFilters({}),
    copyFragranceDetails: (fragrance: Fragrance) => {
      // Same copy function as in main component
      const details = [
        `${fragrance.name} by ${fragrance.house}`,
        fragrance.concentration ? `Concentration: ${fragrance.concentration}` : '',
        fragrance.size_oz || fragrance.size_ml ? `Size: ${fragrance.size_oz ? fragrance.size_oz + ' oz' : ''}${fragrance.size_oz && fragrance.size_ml ? ' / ' : ''}${fragrance.size_ml ? fragrance.size_ml + ' ml' : ''}` : '',
        fragrance.notes_top ? `Top Notes: ${fragrance.notes_top}` : '',
        fragrance.notes_middle ? `Middle Notes: ${fragrance.notes_middle}` : '',
        fragrance.notes_base ? `Base Notes: ${fragrance.notes_base}` : '',
        fragrance.overall_rating ? `Overall Rating: ${fragrance.overall_rating}/10` : '',
        fragrance.longevity ? `Longevity: ${fragrance.longevity}/5` : '',
        fragrance.sillage ? `Sillage: ${fragrance.sillage}/4` : '',
        fragrance.winter_rating ? `Winter Rating: ${fragrance.winter_rating}/5` : '',
        fragrance.spring_rating ? `Spring Rating: ${fragrance.spring_rating}/5` : '',
        fragrance.summer_rating ? `Summer Rating: ${fragrance.summer_rating}/5` : '',
        fragrance.fall_rating ? `Fall Rating: ${fragrance.fall_rating}/5` : '',
        fragrance.day_rating ? `Day Rating: ${fragrance.day_rating}/5` : '',
        fragrance.night_rating ? `Night Rating: ${fragrance.night_rating}/5` : '',
        fragrance.is_favorite ? `Favorite: Yes` : '',
        fragrance.is_decant ? `Type: Decant/Sample` : '',
        fragrance.is_clone && fragrance.original_fragrance ? `Clone of: ${fragrance.original_fragrance}` : '',
      ].filter(Boolean).join('\n');

      // Check if navigator.clipboard is available (HTTPS or localhost)
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(details);
      } else {
        // Fallback for older browsers or HTTP
        try {
          const textArea = document.createElement('textarea');
          textArea.value = details;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand('copy');
          textArea.remove();
        } catch (err) {
          console.error('Failed to copy to clipboard (fallback):', err);
        }
      }
    },
    refreshCollection: async () => {}
  };

  return (
    <PublicFragranceContext.Provider value={value}>
      {children}
    </PublicFragranceContext.Provider>
  );
};

const PublicCollectionView: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const [collection, setCollection] = useState<PublicCollection | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'collection' | 'statistics'>('collection');

  useEffect(() => {
    const fetchPublicCollection = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const API_BASE_URL = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:3001/api`;
        const response = await axios.get(`${API_BASE_URL}/public/collection/${username}`);
        
        if (response.data.success) {
          setCollection(response.data.data);
        } else {
          setError(response.data.message || 'Failed to load collection');
        }
      } catch (err: any) {
        console.error('Error fetching public collection:', err);
        if (err.response?.status === 404) {
          setError('User not found');
        } else if (err.response?.status === 403) {
          setError('This collection is private');
        } else {
          setError('Failed to load collection');
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (username) {
      fetchPublicCollection();
    }
  }, [username]);

  const handleCopyDetails = (fragrance: Fragrance) => {
    // Copy fragrance details (same as private but without personal notes)
    const details = [
      `${fragrance.name} by ${fragrance.house}`,
      fragrance.concentration ? `Concentration: ${fragrance.concentration}` : '',
      fragrance.size_oz || fragrance.size_ml ? `Size: ${fragrance.size_oz ? fragrance.size_oz + ' oz' : ''}${fragrance.size_oz && fragrance.size_ml ? ' / ' : ''}${fragrance.size_ml ? fragrance.size_ml + ' ml' : ''}` : '',
      fragrance.notes_top ? `Top Notes: ${fragrance.notes_top}` : '',
      fragrance.notes_middle ? `Middle Notes: ${fragrance.notes_middle}` : '',
      fragrance.notes_base ? `Base Notes: ${fragrance.notes_base}` : '',
      fragrance.overall_rating ? `Overall Rating: ${fragrance.overall_rating}/10` : '',
      fragrance.longevity ? `Longevity: ${fragrance.longevity}/5` : '',
      fragrance.sillage ? `Sillage: ${fragrance.sillage}/4` : '',
      fragrance.winter_rating ? `Winter Rating: ${fragrance.winter_rating}/5` : '',
      fragrance.spring_rating ? `Spring Rating: ${fragrance.spring_rating}/5` : '',
      fragrance.summer_rating ? `Summer Rating: ${fragrance.summer_rating}/5` : '',
      fragrance.fall_rating ? `Fall Rating: ${fragrance.fall_rating}/5` : '',
      fragrance.day_rating ? `Day Rating: ${fragrance.day_rating}/5` : '',
      fragrance.night_rating ? `Night Rating: ${fragrance.night_rating}/5` : '',
      fragrance.is_favorite ? `Favorite: Yes` : '',
      fragrance.is_decant ? `Type: Decant/Sample` : '',
      fragrance.is_clone && fragrance.original_fragrance ? `Clone of: ${fragrance.original_fragrance}` : '',
    ].filter(Boolean).join('\n');

    // Check if navigator.clipboard is available (HTTPS or localhost)
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(details).then(() => {
        console.log('Fragrance details copied to clipboard');
      }).catch(err => {
        console.error('Failed to copy to clipboard:', err);
      });
    } else {
      // Fallback for older browsers or HTTP
      try {
        const textArea = document.createElement('textarea');
        textArea.value = details;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
        console.log('Fragrance details copied to clipboard (fallback)');
      } catch (err) {
        console.error('Failed to copy to clipboard (fallback):', err);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            {error}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-8">
            {error === 'This collection is private' 
              ? `${username}'s collection is set to private and cannot be viewed.`
              : error === 'User not found'
              ? 'The requested user does not exist.'
              : 'There was an error loading the collection.'
            }
          </p>
          <Button onClick={() => navigate('/')}>
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  if (!collection) {
    return null;
  }

  return (
    <PublicFragranceProvider collection={collection}>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        
        {/* Collection Header */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  {collection.user.username}'s Collection
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mb-2">
                  Member since {new Date(collection.user.memberSince).toLocaleDateString()}
                </p>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                  <p className="text-blue-600 dark:text-blue-400 text-sm">
                    ℹ️ You’re viewing {collection.user.username}'s public collection. Personal notes are private and statistics may be hidden by the owner.
                  </p>
                </div>
              </div>
              <Button onClick={() => navigate('/')} variant="outline">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Close
              </Button>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex space-x-8">
              <button
                onClick={() => setActiveTab('collection')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                  activeTab === 'collection'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <span className="mr-2">🏠</span>
                Collection ({collection.fragrances.length})
              </button>
              <button
                onClick={() => setActiveTab('statistics')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                  activeTab === 'statistics'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <span className="mr-2">📊</span>
                Statistics
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {activeTab === 'collection' ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {/* Search and Filter */}
              <PublicSearchAndFilter />
              
              {/* Collection Grid */}
              <PublicFragranceGrid />
            </motion.div>
           ) : (
             <motion.div
               initial={{ opacity: 0, y: 20 }}
               animate={{ opacity: 1, y: 0 }}
               transition={{ duration: 0.3 }}
             >
               <PublicStatsDashboard />
             </motion.div>
          )}
        </main>
      </div>
    </PublicFragranceProvider>
  );
};

// Public-specific components that use the public context
const PublicSearchAndFilter: React.FC = () => {
  const context = usePublicFragrances();
  const { filters, sorting, setFilters, setSorting, fetchFragrances, getAutocompleteSuggestions, pageSize, setPageSize, currentPage, setCurrentPage } = context;
  
  const [searchQuery, setSearchQuery] = useState(filters.search || '');
  const [localFilters, setLocalFilters] = useState(filters);
  const [localSorting, setLocalSorting] = useState(sorting);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [houseSuggestions, setHouseSuggestions] = useState<string[]>([]);
  const [showHouseSuggestions, setShowHouseSuggestions] = useState(false);
  const [notesSuggestions, setNotesSuggestions] = useState<string[]>([]);
  const [showNotesSuggestions, setShowNotesSuggestions] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState<string[]>([]);
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
  
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    const newFilters = { ...localFilters, search: query || undefined };
    setLocalFilters(newFilters);
    setCurrentPage(1); // Reset to page 1 when searching
    fetchFragrances(newFilters, localSorting);
  };

  const handleFilterChange = (key: string, value: any) => {
    const newFilters = { ...localFilters, [key]: value || undefined };
    setLocalFilters(newFilters);
  };

  const handleSortingChange = (key: string, value: any) => {
    let newSorting = { ...localSorting, [key]: value };
    
    if (key === 'sort_by') {
      // If user switches to a rating field, default to DESC
      if (['winter_rating','spring_rating','summer_rating','fall_rating','day_rating','night_rating','overall_rating','longevity','sillage']
        .includes(value)) {
        newSorting.sort_order = 'desc';
      } else {
        // Otherwise default ASC (name, house, etc.)
        newSorting.sort_order = 'asc';
      }
    }
    
    setLocalSorting(newSorting);
    setSorting(newSorting);
    setCurrentPage(1); // Reset to page 1 when sorting changes
    fetchFragrances(localFilters, newSorting);
  };

  const applyFilters = () => {
    setCurrentPage(1); // Reset to page 1 when applying filters
    fetchFragrances(localFilters, localSorting);
    setIsFilterOpen(false);
  };

  const resetFilters = () => {
    const emptyFilters = {};
    setLocalFilters(emptyFilters);
    setSearchQuery('');
    setCurrentPage(1); // Reset to page 1 when clearing filters
    fetchFragrances(emptyFilters, localSorting);
    setIsFilterOpen(false);
  };
  
  const activeFilterCount = Object.values(localFilters).filter(v => v !== undefined && v !== null && v !== '').length;
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
      {/* Search Bar - Match Dashboard Layout */}
      <div className="flex flex-col gap-4">
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="Search fragrances by name or house..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 items-stretch sm:items-center">
          {/* Sort Dropdown */}
          <div className="flex items-center space-x-2 flex-shrink-0">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 hidden sm:block">
              Sort by:
            </label>
            <select
              value={localSorting.sort_by}
              onChange={(e) => handleSortingChange('sort_by', e.target.value)}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-2 sm:px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 flex-1 sm:flex-none"
            >
              <option value="house">House</option>
              <option value="name">Name</option>
              <option value="overall_rating">Overall Rating</option>
              <option value="longevity">Longevity</option>
              <option value="sillage">Sillage</option>
              <option value="winter_rating">Winter Rating</option>
              <option value="spring_rating">Spring Rating</option>
              <option value="summer_rating">Summer Rating</option>
              <option value="fall_rating">Fall Rating</option>
              <option value="day_rating">Day Rating</option>
              <option value="night_rating">Night Rating</option>
              <option value="updated_at">Last Modified</option>
              <option value="created_at">Date Added</option>
            </select>
            <button
              onClick={() => handleSortingChange('sort_order', localSorting.sort_order === 'asc' ? 'desc' : 'asc')}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors duration-200 bg-gray-100 dark:bg-gray-700 rounded-lg flex-shrink-0"
              title={`Currently: ${localSorting.sort_order === 'asc' ? 'Ascending' : 'Descending'} - Click to toggle`}
            >
              {localSorting.sort_order === 'asc' ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
                </svg>
              )}
            </button>
          </div>

          {/* Filter Toggle */}
          <Button
            variant="outline"
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className="relative flex-shrink-0 inline-flex items-center px-4 py-2 border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Filters
            {activeFilterCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </Button>
        </div>
      </div>

      {/* Filter Panel - Match Dashboard Layout */}
      {isFilterOpen && (
        <div className="pt-6 border-t border-gray-200 dark:border-gray-700 mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
            {/* House Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                House
              </label>
              <input
                type="text"
                placeholder="Filter by house..."
                value={localFilters.house || ''}
                onChange={(e) => handleFilterChange('house', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Notes Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Notes
              </label>
              <input
                type="text"
                placeholder="Filter by notes..."
                value={localFilters.notes || ''}
                onChange={(e) => handleFilterChange('notes', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Concentration Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Concentration
              </label>
              <select
                value={localFilters.concentration || ''}
                onChange={(e) => handleFilterChange('concentration', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">All Concentrations</option>
                <option value="EdC">Eau de Cologne</option>
                <option value="EdT">Eau de Toilette</option>
                <option value="EdP">Eau de Parfum</option>
                <option value="Parfum">Parfum</option>
                <option value="Elixir">Elixir</option>
                <option value="Extrait">Extrait de Parfum</option>
              </select>
            </div>

            {/* Rating Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Min Rating
              </label>
              <select
                value={localFilters.min_rating || ''}
                onChange={(e) => handleFilterChange('min_rating', e.target.value ? parseInt(e.target.value) : undefined)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Any Rating</option>
                {Array.from({ length: 10 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>{i + 1}+ Stars</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Max Rating
              </label>
              <select
                value={localFilters.max_rating || ''}
                onChange={(e) => handleFilterChange('max_rating', e.target.value ? parseInt(e.target.value) : undefined)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Any Rating</option>
                {Array.from({ length: 10 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>{i + 1} Stars</option>
                ))}
              </select>
            </div>
          </div>

          {/* Toggle Filters */}
          <div className="flex flex-wrap gap-4 mb-6">
            <label className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer transition-all duration-200">
              <input
                type="checkbox"
                checked={localFilters.is_favorite === true}
                onChange={(e) => handleFilterChange('is_favorite', e.target.checked ? true : undefined)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">❤️ Favorites only</span>
            </label>

            <label className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer transition-all duration-200">
              <input
                type="checkbox"
                checked={localFilters.is_decant === true}
                onChange={(e) => handleFilterChange('is_decant', e.target.checked ? true : undefined)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">🧪 Decants only</span>
            </label>
          </div>

          {/* Seasonality + Day/Night Filters */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6 space-y-6">
            {/* Seasonality Filters */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Seasons (≥3 rating)
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <label className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer transition-all duration-200">
                  <input
                    type="checkbox"
                    checked={localFilters.winter_rating === true}
                    onChange={(e) => handleFilterChange('winter_rating', e.target.checked ? true : undefined)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">❄️ Winter (3+)</span>
                </label>

                <label className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer transition-all duration-200">
                  <input
                    type="checkbox"
                    checked={localFilters.spring_rating === true}
                    onChange={(e) => handleFilterChange('spring_rating', e.target.checked ? true : undefined)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">🌸 Spring (3+)</span>
                </label>

                <label className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer transition-all duration-200">
                  <input
                    type="checkbox"
                    checked={localFilters.summer_rating === true}
                    onChange={(e) => handleFilterChange('summer_rating', e.target.checked ? true : undefined)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">☀️ Summer (3+)</span>
                </label>

                <label className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer transition-all duration-200">
                  <input
                    type="checkbox"
                    checked={localFilters.fall_rating === true}
                    onChange={(e) => handleFilterChange('fall_rating', e.target.checked ? true : undefined)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">🍂 Fall (3+)</span>
                </label>
              </div>
            </div>

            {/* Day/Night Filters */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Time of Day (≥3 rating)
              </label>
              <div className="grid grid-cols-2 gap-4">
                <label className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer transition-all duration-200">
                  <input
                    type="checkbox"
                    checked={localFilters.day_rating === true}
                    onChange={(e) => handleFilterChange('day_rating', e.target.checked ? true : undefined)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">☀️ Day (3+)</span>
                </label>

                <label className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 cursor-pointer transition-all duration-200">
                  <input
                    type="checkbox"
                    checked={localFilters.night_rating === true}
                    onChange={(e) => handleFilterChange('night_rating', e.target.checked ? true : undefined)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-3 text-sm text-gray-700 dark:text-gray-300 font-medium">🌙 Night (3+)</span>
                </label>
              </div>
            </div>
          </div>

          {/* Filter Actions */}
          <div className="flex items-center space-x-4 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button onClick={applyFilters}>
              Apply Filters
            </Button>
            <Button variant="outline" onClick={resetFilters}>
              Clear All
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const PublicFragranceGrid: React.FC = () => {
  const context = usePublicFragrances();
  const { collection, copyFragranceDetails, currentPage, setCurrentPage, fetchFragrances, filters, sorting } = context;
  
  return (
    <>
      {/* Header with Pagination Info */}
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white text-center sm:text-left">
          Collection
          {collection && (
            <span className="ml-2 text-sm font-normal text-gray-600 dark:text-gray-400">
              ({collection.fragrances.length} of {collection.pagination.total})
            </span>
          )}
        </h2>

        {/* Top Pagination */}
        {collection && collection.pagination.totalPages > 1 && (
          <div className="flex justify-center sm:justify-end flex-wrap gap-2">
            {collection.pagination.page > 1 && (
              <button
                onClick={() => {
                  const newPage = collection.pagination.page - 1;
                  setCurrentPage(newPage);
                  // fetchFragrances(filters, sorting);
                }}
                className="px-3 py-1 rounded border text-sm"
              >
                Previous
              </button>
            )}

            {[...Array(collection.pagination.totalPages)].map((_, idx) => {
              const pageNum = idx + 1;
              return (
                <button
                  key={pageNum}
                  onClick={() => {
                    setCurrentPage(pageNum);
                    // fetchFragrances(filters, sorting);
                  }}
                  className={`px-3 py-1 rounded border text-sm ${
                    collection.pagination.page === pageNum
                      ? "bg-blue-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}

            {collection.pagination.page < collection.pagination.totalPages && (
              <button
                onClick={() => {
                  const newPage = collection.pagination.page + 1;
                  setCurrentPage(newPage);
                  // fetchFragrances(filters, sorting);
                }}
                className="px-3 py-1 rounded border text-sm"
              >
                Next
              </button>
            )}
          </div>
        )}
      </div>

      {/* Grid */}
      <FragranceGrid
        fragrances={collection?.fragrances || []}
        onCopyDetails={copyFragranceDetails}
        // No edit, delete, or favorite toggle for public view
      />

      {/* Bottom Pagination */}
      {collection && collection.pagination.totalPages > 1 && (
        <div className="mt-6 flex justify-center flex-wrap gap-2">
          {collection.pagination.page > 1 && (
            <button
              onClick={() => {
                const newPage = collection.pagination.page - 1;
                setCurrentPage(newPage);
                // fetchFragrances(filters, sorting);
              }}
              className="px-3 py-1 rounded border text-sm"
            >
              Previous
            </button>
          )}

          {[...Array(collection.pagination.totalPages)].map((_, idx) => {
            const pageNum = idx + 1;
            return (
              <button
                key={pageNum}
                onClick={() => {
                  setCurrentPage(pageNum);
                  // fetchFragrances(filters, sorting);
                }}
                className={`px-3 py-1 rounded border text-sm ${
                  collection.pagination.page === pageNum
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-300"
                }`}
              >
                {pageNum}
              </button>
            );
          })}

          {collection.pagination.page < collection.pagination.totalPages && (
            <button
              onClick={() => {
                const newPage = collection.pagination.page + 1;
                setCurrentPage(newPage);
                // fetchFragrances(filters, sorting);
              }}
              className="px-3 py-1 rounded border text-sm"
            >
              Next
            </button>
          )}
        </div>
      )}
    </>
  );
};

const PublicStatsDashboard: React.FC = () => {
  const context = usePublicFragrances();
  const { username } = useParams<{ username: string }>();
  const { stats, collection } = context;
  const statsPrivacy = collection?.statsPrivacy;
  
  if (!stats) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-600 dark:text-gray-400">No statistics available</p>
      </div>
    );
  }

  // Check if all statistics are hidden
  const allStatsHidden = statsPrivacy && !Object.values(statsPrivacy).some(Boolean);
  
  if (allStatsHidden) {
    return (
      <div className="text-center py-16">
        <div className="text-6xl mb-4">🔒</div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          Statistics are Private
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          The owner has chosen to keep their collection statistics private.
        </p>
      </div>
    );
  }
  
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          Collection Statistics
        </h2>
      </div>

      {/* Overview Cards - Show Real Data or Placeholders */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="grid grid-cols-2 lg:grid-cols-4 gap-6 relative"
      >
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
            {stats.totals.fragrances !== null ? stats.totals.fragrances : '••'}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Total Fragrances
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">
            {stats.totals.decants !== null ? stats.totals.decants : '••'}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Decants & Samples
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="text-3xl font-bold text-red-600 dark:text-red-400">
            {stats.totals.favorites !== null ? stats.totals.favorites : '••'}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Favorites
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="text-3xl font-bold text-green-600 dark:text-green-400">
            {stats.totals.houses !== null ? stats.totals.houses : '••'}
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Unique Houses
          </div>
        </div>

        {/* Privacy Overlay for Overview Cards */}
        {statsPrivacy && !statsPrivacy.public_stats_overview && (
          <div className="absolute inset-0 bg-gray-100/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-lg flex items-center justify-center">
            <div className="text-center">
              <div className="text-6xl mb-2">🔒</div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Hidden by Owner
              </div>
            </div>
          </div>
        )}
      </motion.div>

      {/* Collection Insights - Always Show with Privacy Overlay */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 relative"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Collection Insights
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 justify-items-center">
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">{username}'s Collection Profile</h4>
            <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
              <li>
                • {username} has {stats.totals.fragrances !== null ? <span className="text-black dark:text-white">{stats.totals.fragrances}</span> : <span className="blur-sm select-none">••</span>} fragrances from {stats.totals.houses !== null ? <span className="text-black dark:text-white">{stats.totals.houses}</span> : <span className="blur-sm select-none">••</span>} different houses
              </li>
              <li>
                • {stats.totals.favorites !== null && stats.totals.fragrances !== null 
                    ? <span className="text-black dark:text-white">{((stats.totals.favorites / Math.max(stats.totals.fragrances, 1)) * 100).toFixed(1)}</span>
                    : <span className="blur-sm select-none">••</span>}<span className="text-black dark:text-white">%</span> of {username}'s collection are favorites
              </li>
              <li>
                • {stats.totals.decants !== null && stats.totals.fragrances !== null 
                    ? <span className="text-black dark:text-white">{((stats.totals.decants / Math.max(stats.totals.fragrances + stats.totals.decants, 1)) * 100).toFixed(1)}</span>
                    : <span className="blur-sm select-none">••</span>}<span className="text-black dark:text-white">%</span> are decants or samples
              </li>
              {stats.topHouses.length > 0 ? (
                <li>• {username}'s most collected house is <span className="text-black dark:text-white">{stats.topHouses[0].house}</span> with <span className="text-black dark:text-white">{stats.topHouses[0].count}</span> fragrances</li>
              ) : (
                <li>• {username}'s most collected house is <span className="blur-sm select-none">••••••••</span> with <span className="blur-sm select-none">••</span> fragrances</li>
              )}
            </ul>
          </div>
          
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Rating Insights</h4>
            <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
              <li>• Average overall rating: {stats.averageRatings.overall !== null ? <span className="text-black dark:text-white">{stats.averageRatings.overall}</span> : <span className="blur-sm select-none">••</span>} / 10</li>
              <li>• Average longevity: {stats.averageRatings.longevity !== null ? <span className="text-black dark:text-white">{stats.averageRatings.longevity}</span> : <span className="blur-sm select-none">••</span>} / 5</li>
              <li>• Average sillage: {stats.averageRatings.sillage !== null ? <span className="text-black dark:text-white">{stats.averageRatings.sillage}</span> : <span className="blur-sm select-none">••</span>} / 4</li>
              <li>
                • Best season: {
                  stats.averageRatings.seasonal.winter !== null ? (() => {
                    const seasonalRatings = stats.averageRatings.seasonal as Record<string, number>;
                    const bestSeasonEntry = Object.entries(seasonalRatings)
                      .sort(([, a], [, b]) => (b as number) - (a as number))[0];
                    const bestSeasonName = bestSeasonEntry[0].charAt(0).toUpperCase() + bestSeasonEntry[0].slice(1);
                    const bestSeasonRating = bestSeasonEntry[1];
                    return (
                      <>
                        <span className="text-black dark:text-white">{bestSeasonName}</span> -{" "}
                        <span className="text-black dark:text-white">{bestSeasonRating}</span> / 5
                      </>
                    );
                  })() : <span>
                  <span className="blur-sm select-none">••••••</span> (
                  <span className="blur-sm select-none">••</span>/5)
                </span>                
                }
              </li>
            </ul>
          </div>
        </div>

        {/* Privacy Overlay for Collection Insights */}
        {statsPrivacy && !statsPrivacy.public_stats_insights && (
          <div className="absolute inset-0 bg-gray-100/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-lg flex items-center justify-center">
            <div className="text-center">
              <div className="text-6xl mb-2">🔒</div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Hidden by Owner
              </div>
            </div>
          </div>
        )}
      </motion.div>

      {/* Average Ratings - Always Show with Privacy Overlay */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 relative"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Average Ratings
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.averageRatings.overall !== null ? stats.averageRatings.overall : '••'}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Overall Rating (out of 10)
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.averageRatings.longevity !== null ? stats.averageRatings.longevity : '••'}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Longevity (out of 5)
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.averageRatings.sillage !== null ? stats.averageRatings.sillage : '••'}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Sillage (out of 4)
            </div>
          </div>
        </div>

        {/* Privacy Overlay for Average Ratings */}
        {statsPrivacy && !statsPrivacy.public_stats_ratings && (
          <div className="absolute inset-0 bg-gray-100/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-lg flex items-center justify-center">
            <div className="text-center">
              <div className="text-6xl mb-2">🔒</div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Hidden by Owner
              </div>
            </div>
          </div>
        )}
      </motion.div>

      {/* Concentration Types */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mt-6 relative"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Concentration Types
        </h3>

        {stats.concentrations.length > 0 ? (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-6 text-center">
            {['EdC', 'EdT', 'EdP', 'Parfum', 'Elixir', 'Extrait'].map((key) => {
              const concMap: Record<string, string> = {
                EdC: 'Eau de Cologne',
                EdT: 'Eau de Toilette',
                EdP: 'Eau de Parfum',
                Parfum: 'Parfum',
                Elixir: 'Elixir',
                Extrait: 'Extrait de Parfum',
              };

              const concData = stats.concentrations.find((c: any) => c.concentration === key);
              const count = concData ? concData.count : 0;
              const displayName = concMap[key];

              return (
                <div key={key} className="flex flex-col items-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                    {count}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                    {displayName}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-600 dark:text-gray-400 text-sm text-center">
            No concentration data available yet
          </p>
        )}

        {/* Privacy Overlay */}
        {statsPrivacy && !statsPrivacy.public_stats_concentrations && (
          <div className="absolute inset-0 bg-gray-100/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-lg flex items-center justify-center">
            <div className="text-center">
              <div className="text-6xl mb-2">🔒</div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Hidden by Owner</div>
            </div>
          </div>
        )}
      </motion.div>

      {/* Top Houses + Seasonal Preferences */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Top Houses */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 relative"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top Houses</h3>
          {stats.topHouses.length > 0 ? (
            <div className="space-y-3">
              {stats.topHouses.slice(0, 8).map((house: any) => {
                const totalFragrances = stats.totals.fragrances;
                const percentage = (house.count / totalFragrances) * 100;
                return (
                  <div key={house.house} className="flex flex-col space-y-1">
                    <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300 font-medium">
                      <span>{house.house}</span>
                      <span>{house.count}</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 h-2 rounded-full overflow-hidden">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, idx) => (
                <div key={idx} className="flex flex-col space-y-1">
                  <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300 font-medium">
                    <span>••••••••••</span>
                    <span>••</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 h-2 rounded-full overflow-hidden">
                    <div className="bg-blue-600 h-2 rounded-full w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Privacy Overlay */}
          {statsPrivacy && !statsPrivacy.public_stats_houses && (
            <div className="absolute inset-0 bg-gray-100/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-lg flex items-center justify-center">
              <div className="text-center">
                <div className="text-6xl mb-2">🔒</div>
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Hidden by Owner</div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Seasonal Preferences */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 relative"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Seasonal Preferences</h3>
          <div className="space-y-3">
            {Object.entries(stats.averageRatings.seasonal).map(([season, value]) => {
              const percentage = value != null ? (Number(value) / 5) * 100 : 0;
              const colorMap: Record<string, string> = {
                winter: 'bg-blue-400 dark:bg-blue-400',
                spring: 'bg-green-400 dark:bg-green-400',
                summer: 'bg-red-400 dark:bg-red-400',
                fall: 'bg-orange-400 dark:bg-orange-400',
              };
              return (
                <div key={season} className="flex flex-col space-y-1">
                  <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300 font-medium capitalize">
                    <span>{season}</span>
                    <span>{value !== null ? `${value}/5` : '••'}</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 h-2 rounded-full overflow-hidden">
                    <div
                      className={`${colorMap[season]} h-2 rounded-full transition-all duration-500`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Privacy Overlay */}
          {statsPrivacy && !statsPrivacy.public_stats_seasonal && (
            <div className="absolute inset-0 bg-gray-100/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-lg flex items-center justify-center">
              <div className="text-center">
                <div className="text-6xl mb-2">🔒</div>
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Hidden by Owner</div>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Most Common Notes - Always Show with Privacy Overlay */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 relative"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Most Common Notes
        </h3>
        {stats.topNotes && stats.topNotes.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {stats.topNotes.slice(0, 12).map((note: any, index: number) => (
              <div
                key={note.note}
                className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 text-center"
              >
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  {note.note}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {note.count} times
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }, (_, index) => (
              <div
                key={index}
                className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 text-center"
              >
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  ••••••••
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  •• times
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Privacy Overlay for Most Common Notes */}
        {statsPrivacy && !statsPrivacy.public_stats_notes && (
          <div className="absolute inset-0 bg-gray-100/80 dark:bg-gray-900/80 backdrop-blur-sm rounded-lg flex items-center justify-center">
            <div className="text-center">
              <div className="text-6xl mb-2">🔒</div>
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Hidden by Owner
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default PublicCollectionView;
